import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { ChevronDown, Send, Sparkles, Trash2, Wand2 } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { MaterialPreview, type MaterialContent } from "@/components/MaterialPreview";
import {
  MATERIAL_KINDS,
  MATERIAL_STATUSES,
  assignToClass,
  deleteMaterial,
  generateTeacherMaterial,
  listMaterials,
  setMaterialStatus,
  type MaterialKind,
  type MaterialStatus,
} from "@/lib/teacher-content.functions";
import { listTeacherClasses, type ClassRow } from "@/lib/teacher.functions";

export const Route = createFileRoute("/_app/teaching-studio")({
  ssr: false,
  head: () => ({
    meta: [
      { title: "Crea contenuti didattici — Lumen" },
      {
        name: "description",
        content: "Genera verifiche, esercizi, spiegazioni e griglie di valutazione con l'AI e assegnale alla classe.",
      },
      { property: "og:title", content: "Crea contenuti didattici — Lumen" },
      { property: "og:description", content: "Verifiche, esercizi e spiegazioni pronte in un minuto." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
  }),
  component: TeachingStudio,
});

type MaterialRow = {
  id: string;
  kind: string;
  title: string;
  subject: string;
  topic: string;
  difficulty: string;
  body: string;
  content: MaterialContent;
  status: MaterialStatus;
  created_at: string;
};

function TeachingStudio() {
  const { t } = useTranslation();
  const genFn = useServerFn(generateTeacherMaterial);
  const listFn = useServerFn(listMaterials);
  const delFn = useServerFn(deleteMaterial);
  const classesFn = useServerFn(listTeacherClasses);
  const assignFn = useServerFn(assignToClass);
  const statusFn = useServerFn(setMaterialStatus);

  const [form, setForm] = useState({
    kind: "test" as MaterialKind,
    topic: "",
    subject: "",
    level: "",
    difficulty: "medium" as "easy" | "medium" | "hard",
    count: 8,
    minutes: 45,
    notes: "",
    inclusive: true,
    status: "draft" as MaterialStatus,
  });
  const [busy, setBusy] = useState(false);
  const [materials, setMaterials] = useState<MaterialRow[]>([]);
  const [classes, setClasses] = useState<ClassRow[]>([]);
  const [open, setOpen] = useState<string | null>(null);
  const [showKey, setShowKey] = useState<Record<string, boolean>>({});
  const [assignFor, setAssignFor] = useState<MaterialRow | null>(null);
  const [assignForm, setAssignForm] = useState({ classId: "", kind: "homework" as const, due: "", maxScore: 10, instructions: "", adaptive: false });

  useEffect(() => {
    void (async () => {
      const [m, c] = await Promise.all([listFn({ data: {} }), classesFn({ data: undefined } as never)]);
      setMaterials(m as MaterialRow[]);
      setClasses(c);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const generate = async () => {
    if (!form.topic.trim()) return;
    setBusy(true);
    try {
      const row = (await genFn({ data: { ...form, topic: form.topic.trim(), notes: form.notes || undefined } })) as MaterialRow;
      setMaterials((m) => [row, ...m]);
      setOpen(row.id);
      toast.success(t("teacher.studio.generated"));
    } catch (e) {
      toast.error(e instanceof Error ? e.message : t("states.error.title", { defaultValue: "Something went wrong" }));
    } finally {
      setBusy(false);
    }
  };

  const doAssign = async () => {
    if (!assignFor || !assignForm.classId) return;
    setBusy(true);
    try {
      await assignFn({
        data: {
          classId: assignForm.classId,
          materialId: assignFor.id,
          kind: assignForm.kind,
          title: assignFor.title,
          instructions: assignForm.instructions,
          dueDate: assignForm.due || null,
          maxScore: assignForm.maxScore,
          adaptive: assignForm.adaptive,
          status: "published" as const,
        },
      });
      toast.success(t("teacher.assign.done"));
      setAssignFor(null);
    } catch {
      toast.error(t("states.error.title", { defaultValue: "Something went wrong" }));
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="space-y-5 px-4 py-6">
      <header className="space-y-1">
        <h1 className="text-2xl font-bold tracking-tight">{t("teacher.studio.title")}</h1>
        <p className="text-sm text-muted-foreground">{t("teacher.studio.subtitle")}</p>
      </header>

      <Card className="card-soft space-y-4 border-0 p-4">
        <div className="space-y-1.5">
          <Label>{t("teacher.studio.kind")}</Label>
          <div className="flex flex-wrap gap-1.5">
            {MATERIAL_KINDS.map((k) => (
              <button
                key={k}
                type="button"
                onClick={() => setForm({ ...form, kind: k })}
                className={`min-h-9 rounded-full px-3 py-1.5 text-xs font-medium transition ${
                  form.kind === k ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"
                }`}
              >
                {t(`teacher.studio.kinds.${k}`)}
              </button>
            ))}
          </div>
        </div>

        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-1.5 sm:col-span-2">
            <Label htmlFor="m-topic">{t("teacher.studio.topic")}</Label>
            <Input
              id="m-topic"
              value={form.topic}
              placeholder={t("teacher.studio.topicPh")}
              onChange={(e) => setForm({ ...form, topic: e.target.value })}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="m-subject">{t("teacher.studio.subject")}</Label>
            <Input id="m-subject" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="m-level">{t("teacher.studio.level")}</Label>
            <Input id="m-level" value={form.level} onChange={(e) => setForm({ ...form, level: e.target.value })} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="m-count">{t("teacher.studio.count")}</Label>
            <Input
              id="m-count"
              type="number"
              min={1}
              max={30}
              value={form.count}
              onChange={(e) => setForm({ ...form, count: Math.max(1, Math.min(30, Number(e.target.value) || 1)) })}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="m-min">{t("teacher.studio.minutes")}</Label>
            <Input
              id="m-min"
              type="number"
              min={5}
              max={180}
              value={form.minutes}
              onChange={(e) => setForm({ ...form, minutes: Math.max(5, Math.min(180, Number(e.target.value) || 5)) })}
            />
          </div>
        </div>

        <div className="space-y-1.5">
          <Label>{t("teacher.studio.difficulty")}</Label>
          <div className="grid grid-cols-3 gap-1.5 rounded-xl bg-muted p-1">
            {(["easy", "medium", "hard"] as const).map((d) => (
              <button
                key={d}
                type="button"
                onClick={() => setForm({ ...form, difficulty: d })}
                className={`min-h-9 truncate rounded-lg px-2 py-1.5 text-xs font-medium transition ${
                  form.difficulty === d ? "bg-background shadow-sm" : "text-muted-foreground"
                }`}
              >
                {t(`teacher.studio.${d}`)}
              </button>
            ))}
          </div>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="m-notes">{t("teacher.studio.notes")}</Label>
          <Textarea id="m-notes" rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
        </div>

        <div className="flex flex-wrap items-center justify-between gap-2">
          <Label htmlFor="m-incl" className="min-w-0 text-sm">
            {t("teacher.studio.inclusive")}
          </Label>
          <Switch id="m-incl" checked={form.inclusive} onCheckedChange={(v) => setForm({ ...form, inclusive: v })} />
        </div>

        <div className="space-y-1.5">
          <Label>{t("teacher.studio.status")}</Label>
          <div className="flex flex-wrap gap-1.5">
            {MATERIAL_STATUSES.map((st) => (
              <button
                key={st}
                type="button"
                onClick={() => setForm({ ...form, status: st })}
                className={`min-h-9 rounded-full px-3 py-1.5 text-xs font-medium transition ${
                  form.status === st ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"
                }`}
              >
                {t(`teacher.status.${st}`)}
              </button>
            ))}
          </div>
        </div>

        <Button className="min-h-11 w-full" disabled={busy || !form.topic.trim()} onClick={generate}>
          <Wand2 className="mr-2 h-4 w-4" aria-hidden />
          {busy ? t("teacher.studio.generating") : t("teacher.studio.generate")}
        </Button>
      </Card>

      <section className="space-y-3">
        <h2 className="text-lg font-semibold">{t("teacher.studio.library")}</h2>
        {materials.length === 0 ? (
          <Card className="card-soft border-0 p-5 text-sm text-muted-foreground">{t("teacher.studio.empty")}</Card>
        ) : (
          materials.map((m) => {
            const c = m.content ?? {};
            const expanded = open === m.id;
            return (
              <Card key={m.id} className="card-soft space-y-3 border-0 p-4">
                <button
                  type="button"
                  onClick={() => setOpen(expanded ? null : m.id)}
                  className="flex w-full flex-wrap items-start justify-between gap-2 text-left"
                >
                  <div className="min-w-0">
                    <p className="break-words text-sm font-semibold leading-snug">{m.title}</p>
                    <p className="break-words text-xs text-muted-foreground">
                      {m.subject} · {m.topic}
                    </p>
                  </div>
                  <div className="flex shrink-0 items-center gap-2">
                    <Badge variant="secondary">{t(`teacher.studio.kinds.${m.kind}`, { defaultValue: m.kind })}</Badge>
                    <Badge variant={m.status === "published" ? "default" : "outline"}>
                      {t(`teacher.status.${m.status}`, { defaultValue: m.status })}
                    </Badge>
                    <ChevronDown className={`h-4 w-4 transition-transform ${expanded ? "rotate-180" : ""}`} aria-hidden />
                  </div>
                </button>

                {expanded && (
                  <div className="space-y-3">
                    <div className="flex flex-wrap items-center gap-2">
                      {MATERIAL_STATUSES.map((st) => (
                        <Button
                          key={st}
                          size="sm"
                          variant={m.status === st ? "default" : "secondary"}
                          className="min-h-9 rounded-full"
                          onClick={async () => {
                            setMaterials((list) => list.map((x) => (x.id === m.id ? { ...x, status: st } : x)));
                            await statusFn({ data: { id: m.id, status: st } });
                            toast.success(t(`teacher.status.${st}`));
                          }}
                        >
                          {t(`teacher.status.${st}`)}
                        </Button>
                      ))}
                    </div>

                    <MaterialPreview content={c} body={m.body} showAnswers={!!showKey[m.id]} />

                    {!!c.questions?.length && (
                      <Button
                        size="sm"
                        variant="ghost"
                        className="min-h-9"
                        onClick={() => setShowKey({ ...showKey, [m.id]: !showKey[m.id] })}
                      >
                        {t("teacher.studio.answerKey")}
                      </Button>
                    )}

                    <div className="flex flex-wrap gap-2">
                      <Button
                        size="sm"
                        className="min-h-10"
                        onClick={() => {
                          setAssignFor(m);
                          setAssignForm({
                            classId: classes[0]?.id ?? "",
                            kind: "homework",
                            due: "",
                            maxScore: 10,
                            instructions: c.intro ?? "",
                            adaptive: false,
                          });
                        }}
                        disabled={classes.length === 0}
                      >
                        <Send className="mr-1.5 h-3.5 w-3.5" aria-hidden />
                        {t("teacher.studio.assign")}
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="min-h-10 text-destructive"
                        onClick={async () => {
                          await delFn({ data: { id: m.id } });
                          setMaterials((list) => list.filter((x) => x.id !== m.id));
                        }}
                      >
                        <Trash2 className="mr-1.5 h-3.5 w-3.5" aria-hidden />
                        {t("teacher.studio.delete")}
                      </Button>
                    </div>
                  </div>
                )}
              </Card>
            );
          })
        )}
      </section>

      {assignFor && (
        <Card className="card-soft space-y-3 border-0 p-4">
          <h2 className="flex items-center gap-2 text-sm font-semibold">
            <Sparkles className="h-4 w-4" aria-hidden />
            {t("teacher.assign.title")}
          </h2>
          <p className="break-words text-xs text-muted-foreground">{assignFor.title}</p>
          <div className="space-y-1.5">
            <Label htmlFor="a-class">{t("teacher.assign.chooseClass")}</Label>
            <select
              id="a-class"
              value={assignForm.classId}
              onChange={(e) => setAssignForm({ ...assignForm, classId: e.target.value })}
              className="h-11 w-full rounded-xl border border-input bg-background px-3 text-sm"
            >
              {classes.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="a-due">{t("teacher.assign.due")}</Label>
              <Input id="a-due" type="date" value={assignForm.due} onChange={(e) => setAssignForm({ ...assignForm, due: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="a-max">{t("teacher.assign.maxScore")}</Label>
              <Input
                id="a-max"
                type="number"
                min={1}
                max={100}
                value={assignForm.maxScore}
                onChange={(e) => setAssignForm({ ...assignForm, maxScore: Math.max(1, Number(e.target.value) || 1) })}
              />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="a-instr">{t("teacher.assign.instructions")}</Label>
            <Textarea
              id="a-instr"
              rows={2}
              value={assignForm.instructions}
              onChange={(e) => setAssignForm({ ...assignForm, instructions: e.target.value })}
            />
          </div>
          <label className="flex items-start gap-3 rounded-2xl bg-muted/40 p-3">
            <Switch
              checked={assignForm.adaptive}
              onCheckedChange={(v) => setAssignForm({ ...assignForm, adaptive: v })}
              aria-label={t("assess.adaptive")}
            />
            <span className="min-w-0">
              <span className="block text-sm font-medium">{t("assess.adaptive")}</span>
              <span className="block text-xs text-muted-foreground">{t("assess.adaptiveHint")}</span>
            </span>
          </label>
          <div className="flex flex-wrap gap-2">
            <Button className="min-h-11 flex-1" disabled={busy || !assignForm.classId} onClick={doAssign}>
              {t("teacher.assign.confirm")}
            </Button>
            <Button variant="ghost" className="min-h-11" onClick={() => setAssignFor(null)}>
              ×
            </Button>
          </div>
        </Card>
      )}

      <Button asChild variant="ghost" className="min-h-10 w-full">
        <Link to="/teacher">{t("teacher.dash.title")}</Link>
      </Button>
    </main>
  );
}
